/**
 * Share management for one prototype. Minting a public link is a staged human
 * act (glass): immediate, logged server-side, and revocable. Access otherwise
 * follows the prototype's own audience.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Copy, Link2 } from "lucide-react";
import { workItemUrl } from "@yagni/shared";
import { Button } from "../ui/index.js";
import { formatRelative } from "../lib/relativeTime.js";

export interface ShareState {
  shared: boolean;
  url: string | null;
  createdAt: string | null;
  createdByName: string | null;
}

export function SharePanel({
  prototypeId,
  share,
  onShareChange,
  workItemId,
}: {
  prototypeId: string;
  /** The share facts the page holds; it refreshes them with the prototype. */
  share: ShareState;
  onShareChange: (share: ShareState) => void;
  workItemId: string | null;
}) {
  const setShare = onShareChange;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const shareUrl = `/api/prototypes/${encodeURIComponent(prototypeId)}/share`;

  const mintShare = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(shareUrl, { method: "POST", credentials: "include" });
      if (!res.ok) {
        setError("Could not create the link. Try again.");
        return;
      }
      const body = (await res.json()) as { shareUrl?: string };
      setShare({ shared: true, url: body.shareUrl ?? null, createdAt: new Date().toISOString(), createdByName: "you" });
    } catch {
      setError("Could not create the link. Check your connection.");
    } finally {
      setBusy(false);
    }
  }, [shareUrl, setShare]);

  const revokeShare = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(shareUrl, { method: "DELETE", credentials: "include" });
      if (!res.ok) {
        setError("Could not revoke the link. Try again.");
        return;
      }
      setShare({ shared: false, url: null, createdAt: null, createdByName: null });
      setCopied(false);
    } catch {
      setError("Could not revoke the link. Check your connection.");
    } finally {
      setBusy(false);
    }
  }, [shareUrl, setShare]);

  // The copied-checkmark reset timer, cleared on unmount (and before a
  // re-copy) so a late fire never sets state on an unmounted panel.
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    },
    [],
  );

  const copyShareUrl = useCallback(async () => {
    if (!share.url) return;
    try {
      await navigator.clipboard.writeText(share.url);
      setCopied(true);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Selection stays possible; the URL is visible in full.
    }
  }, [share.url]);

  return (
    <section className="material-glass px-4 py-3" data-testid="prototype-share-panel">
      <div className="font-mono text-[10px] font-bold uppercase tracking-widest text-[var(--text-label)] mb-2">
        Sharing
      </div>
      {share.shared ? (
        <div className="flex flex-col gap-2">
          {share.url ? (
            <div className="flex items-center gap-2">
              <span className="font-mono text-[11.5px] text-[var(--text-primary)] break-all flex-1 min-w-0">
                {share.url}
              </span>
              <Button type="button" variant="secondary" size="sm" onClick={() => void copyShareUrl()}>
                {copied ? <Check width={13} height={13} aria-hidden /> : <Copy width={13} height={13} aria-hidden />}
                Copy link
              </Button>
            </div>
          ) : (
            <p className="text-[12.5px] text-[var(--text-secondary)] m-0">A public link is active for this prototype.</p>
          )}
          <p className="font-mono text-[10.5px] text-[var(--text-tertiary)] m-0">
            Public link
            {share.createdAt ? ` · created ${formatRelative(share.createdAt)}` : ""}
            {share.createdByName ? ` by ${share.createdByName}` : ""}
          </p>
          <div>
            <Button type="button" variant="secondary" size="sm" onClick={() => void revokeShare()} disabled={busy}>
              Revoke link
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <p className="text-[12.5px] text-[var(--text-secondary)] m-0">
            Access follows this prototype’s permissions. A public link lets anyone with the URL view it, and you can
            revoke it at any time.
          </p>
          <div>
            <Button type="button" variant="primary" size="sm" onClick={() => void mintShare()} disabled={busy}>
              <Link2 width={13} height={13} aria-hidden />
              Create public link
            </Button>
          </div>
        </div>
      )}
      {error && (
        <p className="text-[12px] text-[var(--error)] mt-2 mb-0" role="alert">
          {error}
        </p>
      )}
      {workItemId && (
        <p className="text-[11.5px] text-[var(--text-tertiary)] mt-3 mb-0">
          Attached to{" "}
          <Link to={workItemUrl(workItemId)} className="text-[var(--accent)] no-underline hover:underline">
            its ticket
          </Link>
          .
        </p>
      )}
    </section>
  );
}
